import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import {
  FactCheckIcon,
  HourglassEmptyIcon as PendingIcon,
  MailOutlineIcon,
  SendIcon,
  TimerOffIcon,
  RadioButtonUncheckedIcon as UnverifiedIcon,
  VerifiedIcon,
} from "../../../icons";
import { restFetch } from '../../../api/apiClient';
import { useRootStore } from '../../../store/contexts';
import { graphqlToDate } from '../../../common/utils';

/** La verificación de un email (una por empresa y email), como la devuelve la consulta. */
export interface VerificationRow {
  status: string;
  verifiedAt?: string | null;
  sentAt?: string | null;
  lastSentAt?: string | null;
  expiresAt?: string | null;
  // Lo que pide el enlace vigente ('email' | 'data'). Sin el campo en el proyecto, o
  // en un registro de antes del campo, se deduce de sourceEntity y sourceRecordId.
  scope?: string | null;
  sourceEntity?: string | null;
  sourceRecordId?: string | null;
}

/** La última validación de datos hecha para el registro (con cualquier email). */
export interface DataValidationRow {
  validationSubmittedAt?: string | null;
}

/** El estado del email: el del enlace vigente. */
export type EmailState = 'none' | 'pending' | 'verified' | 'expired' | 'revoked';

/** El estado de los datos del registro. */
export type DataState =
  | { kind: 'pending'; sentAt?: string | null; lastValidatedAt?: string | null }
  | { kind: 'validated'; at: string }
  | { kind: 'expired' }
  | { kind: 'none' };

/** Lo que pide el enlace vigente, como verificationScopeOf en el server. */
export function scopeOf(row: VerificationRow): 'email' | 'data' {
  if (row.scope === 'email' || row.scope === 'data') return row.scope;
  return row.sourceEntity && row.sourceRecordId ? 'data' : 'email';
}

/**
 * El estado del email. Un enlace pendiente que ya venció cuenta como vencido
 * (el server lo pasa a expired al rato).
 */
export function estadoDelEmail(row: VerificationRow | null | undefined, now: Date = new Date()): EmailState {
  if (!row) return 'none';
  const status = String(row.status).toLowerCase();
  if (status === 'pending') {
    return row.expiresAt && new Date(row.expiresAt).getTime() < now.getTime() ? 'expired' : 'pending';
  }
  if (status === 'verified' || status === 'expired' || status === 'revoked') return status;
  return 'none';
}

/**
 * El estado de los datos del registro, en este orden:
 * 1. pendiente: el enlace vigente pide los datos de este registro y no venció
 *    (con la fecha de la validación anterior, si la hubo);
 * 2. confirmados: hay una validación hecha para este registro, con cualquier
 *    email (cambiar el email no la invalida);
 * 3. vencida: el enlace pedía los datos de este registro y venció;
 * 4. sin confirmar.
 * Un email verificado por otro origen (otro registro, o solo el email) no
 * confirma los datos.
 */
export function estadoDeLosDatos(
  row: VerificationRow | null | undefined,
  datos: DataValidationRow | null | undefined,
  entityName: string,
  recordId: string | undefined,
  now: Date = new Date()
): DataState {
  const forThisRecord =
    !!row && !!recordId && scopeOf(row) === 'data' && row.sourceEntity === entityName && row.sourceRecordId === recordId;
  const email = estadoDelEmail(row, now);
  if (forThisRecord && email === 'pending') {
    return { kind: 'pending', sentAt: row!.lastSentAt || row!.sentAt, lastValidatedAt: datos?.validationSubmittedAt ?? null };
  }
  if (datos?.validationSubmittedAt) return { kind: 'validated', at: datos.validationSubmittedAt };
  if (forThisRecord && email === 'expired') return { kind: 'expired' };
  return { kind: 'none' };
}

/** Los minutos que faltan hasta la fecha (redondeados para arriba); 0 si ya pasó o no hay. */
const minutesUntil = (iso?: string | null): number => {
  if (!iso) return 0;
  const diffMs = new Date(iso).getTime() - Date.now();
  return diffMs > 0 ? Math.ceil(diffMs / 60000) : 0;
};

// Si el proyecto declara EmailVerification.scope. Se sabe con la primera consulta:
// sin el campo, el server la rechaza (400) y se repite sin él, deduciendo el alcance.
// Exportado para que las pruebas lo vuelvan a undefined.
export const scopeFieldKnown: { value: boolean | undefined } = { value: undefined };

const ROW_FIELDS = 'status verifiedAt sentAt lastSentAt expiresAt sourceEntity sourceRecordId';

interface EmailVerificationIndicatorProps {
  email: string | null | undefined;
  entityName: string;
  recordId?: string;
  validationMode?: boolean;
  // Cuando el form de admin tiene cambios sin guardar, el envío se bloquea: el
  // link generaría el formulario ad-hoc con los datos de la DB (viejos), no con
  // lo que el operador ve en pantalla.
  hasPendingChanges?: boolean;
}

type Scope = 'email' | 'data';

export const EmailVerificationIndicator: React.FC<EmailVerificationIndicatorProps> = ({
  email,
  entityName,
  recordId,
  validationMode,
  hasPendingChanges,
}) => {
  const rootStore = useRootStore();
  const { langStore } = rootStore;
  const [row, setRow] = useState<VerificationRow | null>(null);
  const [datos, setDatos] = useState<DataValidationRow | null>(null);
  const [retryAfter, setRetryAfter] = useState<string | undefined>();
  const [sending, setSending] = useState<Scope | null>(null);
  // La confirmación antes de enviar. first: a ese email no hay nada vivo (o, en los
  // datos, nunca se pidieron): el diálogo dice «Enviar», no «Reenviar».
  const [confirm, setConfirm] = useState<{ text: string; scope: Scope; first: boolean } | null>(null);

  const fetchStatus = useCallback(async () => {
    if (!email) return;
    const withDatos = !!validationMode && !!recordId;
    const query = (withScope: boolean) => `query EmailVerificationCheck($porEmail: EmailVerification_Filter!${
      withDatos ? ', $datos: EmailVerification_Filter!' : ''
    }) {
      porEmail: emailVerificationListWithOffset(where: $porEmail, offset: 0, take: 1) {
        ${ROW_FIELDS}${withScope ? ' scope' : ''}
      }${
        withDatos
          ? `
      datos: emailVerificationListWithOffset(where: $datos, orderBy: [{ validationSubmittedAt: desc }], offset: 0, take: 1) {
        validationSubmittedAt
      }`
          : ''
      }
    }`;
    const variables = {
      porEmail: { email: { equals: email.toLowerCase().trim() } },
      ...(withDatos
        ? {
            datos: {
              sourceEntity: { equals: entityName },
              sourceRecordId: { equals: recordId },
              validationSubmittedAt: { not: null },
            },
          }
        : {}),
    };
    const run = (withScope: boolean) =>
      restFetch('/graphql', { method: 'POST', body: JSON.stringify({ query: query(withScope), variables }) });

    try {
      let data: any;
      // Sin validationMode no hace falta el alcance: solo cuenta el estado del email.
      if (!validationMode || scopeFieldKnown.value === false) {
        data = await run(false);
      } else {
        try {
          data = await run(true);
          scopeFieldKnown.value = true;
        } catch (err) {
          // Un 400 es la consulta rechazada: el proyecto no declara scope.
          if (scopeFieldKnown.value !== undefined || !/^HTTP 400/.test(String((err as Error)?.message))) throw err;
          data = await run(false);
          scopeFieldKnown.value = false;
        }
      }
      setRow(data?.data?.porEmail?.[0] ?? null);
      setDatos(data?.data?.datos?.[0] ?? null);
    } catch {
      // Silently fail — show "none" status
    }
  }, [email, entityName, recordId, validationMode]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  useEffect(() => {
    const handler = () => { fetchStatus(); };
    window.addEventListener('emailVerificationChanged', handler);
    window.addEventListener('externalValidationSubmitted', handler);
    return () => {
      window.removeEventListener('emailVerificationChanged', handler);
      window.removeEventListener('externalValidationSubmitted', handler);
    };
  }, [fetchStatus]);

  // Lo que el operador ve si el envío no salió: la espera, si el server la dice, o
  // el motivo. restFetch tira con toda respuesta que no es 2xx: el mensaje es el
  // código del server y el cuerpo viene en err.body.
  const noticeOf = (err: unknown): { message: string; severity: 'warning' | 'error' } => {
    const code = String((err as Error | undefined)?.message ?? '');
    switch (code) {
      case 'rate_limited': {
        const mins = minutesUntil((err as { body?: { retryAfter?: string } }).body?.retryAfter);
        return {
          message: mins
            ? `${langStore.emailSendRateLimited} ${langStore.emailSendRetryIn} ${mins} min.`
            : langStore.emailSendRateLimited,
          severity: 'warning',
        };
      }
      case 'data_validation_pending':
        return { message: langStore.emailSendDataValidationPending, severity: 'warning' };
      case 'scope_not_supported':
        return { message: langStore.emailSendScopeNotSupported, severity: 'warning' };
      case 'concurrent_send':
        return { message: langStore.emailSendConflict, severity: 'warning' };
      default:
        return { message: code ? `${langStore.emailSendFailed} (${code}).` : `${langStore.emailSendFailed}.`, severity: 'error' };
    }
  };

  const handleSend = async (scope: Scope, forceResend = false) => {
    if (!email || sending || hasPendingChanges) return;
    setSending(scope);

    try {
      const body: Record<string, any> = { email, sourceEntity: entityName, scope };
      if (recordId) {
        body.sourceRecordId = recordId;
      }
      if (forceResend) {
        body.resend = true;
      }
      await restFetch('/api/email-verification/send', {
        method: 'POST',
        body: JSON.stringify(body),
      });
    } catch (err) {
      if ((err as Error | undefined)?.message === 'rate_limited') {
        setRetryAfter((err as { body?: { retryAfter?: string } }).body?.retryAfter);
      }
      const { message, severity } = noticeOf(err);
      rootStore.showSnackbar(message, severity);
    } finally {
      setSending(null);
      // Lo que quedó guardado (enviada, ya verificado o ya validado): se vuelve a leer.
      fetchStatus();
    }
  };

  if (!email) return null;

  // DD/MM/AAAA: sin locale, un navegador en inglés pondría el mes primero.
  const formatDate = (iso?: string | null) => {
    if (!iso) return '';
    return graphqlToDate(iso);
  };

  const emailState = estadoDelEmail(row);
  const isRateLimited = !!retryAfter && new Date(retryAfter) > new Date();
  const blocked = !!hasPendingChanges;
  // En create (modo validación de datos, sin registro guardado) el envío NO se
  // ofrece: el link genera el formulario ad-hoc desde el registro, que aún no existe.
  const createMode = !!validationMode && !recordId;

  const formatRetryWait = () => {
    const mins = minutesUntil(retryAfter);
    return mins ? `${langStore.emailResendAvailableAt} ${mins} min` : '';
  };

  // Nada vivo a ese email (ni pendiente ni verificado): el primer envío.
  const emailSent = emailState === 'verified' || emailState === 'pending';
  const ask = (text: string, scope: Scope, first = !emailSent) => setConfirm({ text, scope, first });

  const actionButton = (args: {
    scope: Scope;
    title: string;
    icon: React.ReactNode;
    onClick: () => void;
    disabled: boolean;
  }) => (
    <Tooltip title={args.title}>
      <span>
        <IconButton size="small" onClick={args.onClick} disabled={args.disabled} sx={{ ml: 0.5 }}>
          {sending === args.scope ? <CircularProgress size={16} /> : args.icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  const emailChip = (() => {
    switch (emailState) {
      case 'verified':
        return (
          <Tooltip title={`${langStore.emailVerified} ${formatDate(row?.verifiedAt)}`}>
            <Chip icon={<VerifiedIcon />} label={langStore.emailVerified} color="success" size="small" variant="outlined" />
          </Tooltip>
        );
      case 'pending':
        return (
          <Tooltip title={`${langStore.emailPending} (${langStore.emailSentAt} ${formatDate(row?.sentAt || row?.lastSentAt)})`}>
            <Chip icon={<PendingIcon />} label={langStore.emailPending} color="warning" size="small" variant="outlined" />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title={langStore.emailNotVerified}>
            <Chip
              icon={<UnverifiedIcon />}
              label={langStore.emailNotVerified}
              size="small"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          </Tooltip>
        );
    }
  })();

  const confirmTitle = !confirm?.first
    ? langStore.emailResendConfirmTitle
    : confirm.scope === 'data'
    ? langStore.emailSendValidation
    : langStore.emailSendVerification;

  const confirmDialog = (
    <Dialog open={!!confirm} onClose={() => setConfirm(null)}>
      <DialogTitle>{confirmTitle}</DialogTitle>
      <DialogContent>
        <DialogContentText>{confirm?.text}</DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => setConfirm(null)}>{langStore.cancelCommand}</Button>
        <Button
          onClick={() => {
            const scope = confirm?.scope;
            setConfirm(null);
            if (scope) handleSend(scope, true);
          }}
          variant="contained"
        >
          {confirmTitle}
        </Button>
      </DialogActions>
    </Dialog>
  );

  // Sin validación de datos (un contacto que solo verifica su email): un chip y un ícono.
  if (!validationMode) {
    const sent = emailSent;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
        {emailChip}
        {actionButton({
          scope: 'email',
          title: blocked
            ? langStore.emailSendPendingChangesVerification
            : emailState === 'pending' && isRateLimited
            ? formatRetryWait()
            : sent ? langStore.emailResendVerification : langStore.emailSendVerification,
          icon: <SendIcon fontSize="small" />,
          onClick: () =>
            emailState === 'verified'
              ? ask(langStore.emailResendConfirmAlreadyVerified, 'email')
              : emailState === 'pending'
              ? ask(langStore.emailResendConfirmPending, 'email')
              : handleSend('email'),
          disabled: !!sending || blocked || (emailState === 'pending' && isRateLimited),
        })}
        {confirmDialog}
      </Box>
    );
  }

  // Con validación de datos: el chip del email y su ícono (verificar solo el
  // email), y el chip de los datos y su ícono (validar email y datos). En el alta,
  // solo el chip del email.
  const dataState = estadoDeLosDatos(row, datos, entityName, recordId);

  const dataChip = (() => {
    switch (dataState.kind) {
      case 'pending':
        return (
          <Tooltip
            title={`${langStore.emailDataPending} (${langStore.emailSentAt} ${formatDate(dataState.sentAt)})${
              dataState.lastValidatedAt ? ` · ${langStore.emailDataLastValidation} ${formatDate(dataState.lastValidatedAt)}` : ''
            }`}
          >
            <Chip icon={<PendingIcon />} label={langStore.emailDataPending} color="warning" size="small" variant="outlined" />
          </Tooltip>
        );
      case 'validated':
        return (
          <Tooltip title={`${langStore.emailDataValidatedOn} ${formatDate(dataState.at)}`}>
            <Chip icon={<VerifiedIcon />} label={langStore.emailDataValidated} color="success" size="small" variant="outlined" />
          </Tooltip>
        );
      case 'expired':
        return (
          <Tooltip title={langStore.emailDataExpired}>
            <Chip
              icon={<TimerOffIcon />}
              label={langStore.emailDataExpired}
              size="small"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          </Tooltip>
        );
      default:
        return (
          <Tooltip title={langStore.emailDataNotValidated}>
            <Chip
              icon={<UnverifiedIcon />}
              label={langStore.emailDataNotValidated}
              size="small"
              variant="outlined"
              sx={{ color: 'text.secondary' }}
            />
          </Tooltip>
        );
    }
  })();

  const dataPending = dataState.kind === 'pending';
  // Una validación de datos pendiente con este email, de este registro o de otro
  // (un email compartido): verificar solo el email la reemplazaría, y el server la
  // rechaza (409 data_validation_pending).
  const anyDataPending = !!row && emailState === 'pending' && scopeOf(row) === 'data';

  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', ml: 1 }}>
      {emailChip}
      {!createMode &&
        actionButton({
          scope: 'email',
          // Deshabilitado con una validación de datos pendiente con este email (de
          // este registro o de otro): también verifica el email, y un envío de solo
          // email la reemplazaría (el enlace es el mismo).
          title: anyDataPending
            ? langStore.emailVerifyOnlyBlockedByData
            : blocked
            ? langStore.emailSendPendingChangesVerification
            : emailState === 'pending' && isRateLimited
            ? formatRetryWait()
            : emailSent ? langStore.emailResendVerifyOnly : langStore.emailVerifyOnly,
          icon: <MailOutlineIcon fontSize="small" />,
          onClick: () =>
            dataState.kind === 'validated'
              ? ask(langStore.emailResendConfirmVerifyOnly, 'email')
              : emailState === 'verified'
              ? ask(langStore.emailResendConfirmAlreadyVerified, 'email')
              : emailState === 'pending'
              ? ask(langStore.emailResendConfirmPending, 'email')
              : handleSend('email'),
          disabled: !!sending || blocked || anyDataPending || (emailState === 'pending' && isRateLimited),
        })}
      {!createMode && <Box sx={{ ml: 1, display: 'inline-flex' }}>{dataChip}</Box>}
      {!createMode &&
        actionButton({
          scope: 'data',
          title: blocked
            ? langStore.emailSendPendingChanges
            : dataState.kind === 'none'
            ? langStore.emailValidateData
            : langStore.emailResendValidateData,
          icon: <FactCheckIcon fontSize="small" />,
          // Con algo vivo a ese email (pendiente o verificado), siempre con
          // confirmación y como reenvío: el pedido de datos lo deja pendiente, y sin
          // resend el server aplicaría la espera entre envíos (429).
          onClick: () =>
            dataState.kind === 'validated'
              ? ask(langStore.emailResendConfirmAlreadyValidated, 'data')
              : dataPending
              ? ask(langStore.emailResendConfirmDataPending, 'data')
              : emailSent
              ? ask(langStore.emailResendConfirmValidateData, 'data', dataState.kind === 'none')
              : handleSend('data'),
          disabled: !!sending || blocked,
        })}
      {confirmDialog}
    </Box>
  );
};
